import React, { useState, useEffect, useMemo } from 'react';
import api, { trackActivity, portfolioRegistryAPI } from '../services/api';
import { useTheme } from '../contexts/ThemeContext';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import './PortfolioConstructorApp.css';
import ManagedDisclaimer from '../components/ManagedDisclaimer';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const PortfolioConstructorApp = () => {
  const { theme } = useTheme();

  // State for fund selection
  const [availableFunds, setAvailableFunds] = useState([]);
  const [assetClasses, setAssetClasses] = useState([]);
  // Canonical broad-asset-class order from the live taxonomy. Empty until the
  // registry call lands; the fallback below tacks unknowns onto the end.
  const [broadClassOrder, setBroadClassOrder] = useState([]);
  const [isLoadingFunds, setIsLoadingFunds] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [selectedAssetClass, setSelectedAssetClass] = useState('');

  // State for portfolio
  const [portfolio, setPortfolio] = useState([]); // [{tr_code, name, weight}]
  const [benchmarkTrCode, setBenchmarkTrCode] = useState('');
  const [benchmarkSearch, setBenchmarkSearch] = useState('');
  const [showBenchmarkDropdown, setShowBenchmarkDropdown] = useState(false);
  const [priorityBenchmarks, setPriorityBenchmarks] = useState(new Set());

  // State for results
  const [results, setResults] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [error, setError] = useState(null);

  // Track app access and load available funds on mount
  useEffect(() => {
    trackActivity('app_access', 'portfolio_constructor');
    loadFunds();
    loadBenchmarkTickers();
    loadBroadClassOrder();
  }, []);

  const loadBroadClassOrder = async () => {
    try {
      const data = await portfolioRegistryAPI.getRegistry();
      const order = (data?.broad_asset_classes || []).map((b) => b.name);
      setBroadClassOrder(order);
    } catch (err) {
      console.error('Failed to load broad asset class order from registry:', err);
    }
  };

  const loadFunds = async () => {
    try {
      const response = await api.get('/api/constructor/funds');
      setAvailableFunds(response.data.funds);
      setAssetClasses(response.data.asset_classes);
    } catch (error) {
      console.error('Error loading funds:', error);
      setError('Failed to load funds');
    } finally {
      setIsLoadingFunds(false);
    }
  };

  const loadBenchmarkTickers = async () => {
    try {
      const response = await api.get('/api/constructor/benchmark-tickers');
      setPriorityBenchmarks(new Set(response.data.benchmark_tickers));
    } catch (error) {
      console.error('Error loading benchmark tickers:', error);
    }
  };

  // Filter funds by search and asset class
  const filteredFunds = useMemo(() => {
    let filtered = availableFunds;

    if (selectedAssetClass) {
      filtered = filtered.filter(f => f.asset_class_group === selectedAssetClass);
    }

    if (searchText) {
      const search = searchText.toLowerCase();
      filtered = filtered.filter(f =>
        f.name.toLowerCase().includes(search) ||
        f.tr_code.toLowerCase().includes(search)
      );
    }

    return filtered;
  }, [availableFunds, selectedAssetClass, searchText]);

  // Group filtered funds by asset class
  const groupedFunds = useMemo(() => {
    const groups = {};
    filteredFunds.forEach(fund => {
      const group = fund.asset_class_group || 'Other';
      if (!groups[group]) {
        groups[group] = [];
      }
      groups[group].push(fund);
    });

    // Sort groups using the live taxonomy's broad-class display_order; any
    // fund asset_class_group not present in the taxonomy is appended at the
    // end in the order it appeared (e.g. AP_List legacy labels).
    const sortedGroups = {};
    broadClassOrder.forEach(ac => {
      if (groups[ac]) {
        sortedGroups[ac] = groups[ac];
      }
    });
    Object.keys(groups).forEach(ac => {
      if (!sortedGroups[ac]) {
        sortedGroups[ac] = groups[ac];
      }
    });

    return sortedGroups;
  }, [filteredFunds, broadClassOrder]);

  // Calculate total weight
  const totalWeight = useMemo(() => {
    return portfolio.reduce((sum, f) => sum + (parseFloat(f.weight) || 0), 0);
  }, [portfolio]);

  // Filter and sort benchmark funds - priority benchmarks first, then by search
  const filteredBenchmarkFunds = useMemo(() => {
    let filtered = availableFunds;

    // Apply search filter
    if (benchmarkSearch) {
      const search = benchmarkSearch.toLowerCase();
      filtered = filtered.filter(f =>
        f.name.toLowerCase().includes(search) ||
        f.tr_code.toLowerCase().includes(search)
      );
    }

    // Sort with priority benchmarks first
    return [...filtered].sort((a, b) => {
      const aIsPriority = priorityBenchmarks.has(a.tr_code);
      const bIsPriority = priorityBenchmarks.has(b.tr_code);

      if (aIsPriority && !bIsPriority) return -1;
      if (!aIsPriority && bIsPriority) return 1;
      return a.name.localeCompare(b.name);
    });
  }, [availableFunds, benchmarkSearch, priorityBenchmarks]);

  // Select benchmark
  const selectBenchmark = (fund) => {
    setBenchmarkTrCode(fund.tr_code);
    setBenchmarkSearch(fund.name);
    setShowBenchmarkDropdown(false);
    setResults(null);
  };

  // Clear benchmark
  const clearBenchmark = () => {
    setBenchmarkTrCode('');
    setBenchmarkSearch('');
    setShowBenchmarkDropdown(false);
    setResults(null);
  };

  // Add fund to portfolio
  const addToPortfolio = (fund) => {
    if (portfolio.some(f => f.tr_code === fund.tr_code)) {
      return; // Already in portfolio
    }
    setPortfolio(prev => [...prev, { tr_code: fund.tr_code, name: fund.name, weight: 0 }]);
    setResults(null); // Clear results when portfolio changes
  };

  // Remove fund from portfolio
  const removeFromPortfolio = (trCode) => {
    setPortfolio(prev => prev.filter(f => f.tr_code !== trCode));
    setResults(null);
  };

  // Update fund weight
  const updateWeight = (trCode, weight) => {
    const numWeight = parseFloat(weight) || 0;
    setPortfolio(prev => prev.map(f =>
      f.tr_code === trCode ? { ...f, weight: numWeight } : f
    ));
    setResults(null);
  };

  // Calculate portfolio
  const calculatePortfolio = async () => {
    if (portfolio.length === 0) {
      setError('Please add at least one fund to the portfolio');
      return;
    }

    if (Math.abs(totalWeight - 100) > 0.01) {
      setError('Weights must sum to 100%');
      return;
    }

    setIsCalculating(true);
    setError(null);

    try {
      const response = await api.post('/api/constructor/calculate', {
        portfolio: portfolio.map(f => ({ tr_code: f.tr_code, weight: f.weight })),
        benchmark_tr_code: benchmarkTrCode || null
      });
      setResults(response.data);
    } catch (error) {
      console.error('Error calculating portfolio:', error);
      setError(error.response?.data?.detail || 'Failed to calculate portfolio');
    } finally {
      setIsCalculating(false);
    }
  };

  // Clear portfolio
  const clearPortfolio = () => {
    setPortfolio([]);
    setBenchmarkTrCode('');
    setResults(null);
    setError(null);
  };

  // Rebalance weights to 100%
  const rebalanceWeights = () => {
    if (portfolio.length === 0) return;

    const currentTotal = portfolio.reduce((sum, f) => sum + (parseFloat(f.weight) || 0), 0);

    if (currentTotal === 0) {
      // If all weights are 0, distribute equally
      const equalWeight = 100 / portfolio.length;
      setPortfolio(prev => prev.map(f => ({ ...f, weight: parseFloat(equalWeight.toFixed(2)) })));
    } else {
      // Scale existing weights proportionally to sum to 100%
      const scaleFactor = 100 / currentTotal;
      setPortfolio(prev => prev.map(f => ({
        ...f,
        weight: parseFloat(((parseFloat(f.weight) || 0) * scaleFactor).toFixed(2))
      })));
    }
    setResults(null);
  };

  // Chart configuration
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: theme === 'dark' ? '#e0e0e0' : '#333',
        },
      },
      tooltip: {
        callbacks: {
          label: (context) => `${context.dataset.label}: ${context.parsed.y.toFixed(2)}%`
        }
      }
    },
    scales: {
      x: {
        ticks: {
          color: theme === 'dark' ? '#e0e0e0' : '#666',
          maxTicksLimit: 12,
        },
        grid: {
          color: theme === 'dark' ? '#444' : '#e0e0e0',
        },
      },
      y: {
        ticks: {
          color: theme === 'dark' ? '#e0e0e0' : '#666',
          callback: (value) => `${value}%`
        },
        grid: {
          color: theme === 'dark' ? '#444' : '#e0e0e0',
        },
      },
    },
  };

  // Build chart data
  const chartData = useMemo(() => {
    if (!results) return null;

    const portfolioSeries = results.portfolio.cumulative_series;
    const labels = portfolioSeries.map(p => {
      const date = new Date(p.date);
      return date.toLocaleDateString('en-AU', { month: 'short', year: '2-digit' });
    });

    const datasets = [
      {
        label: 'Portfolio',
        data: portfolioSeries.map(p => p.value),
        borderColor: '#072040',
        backgroundColor: 'rgba(0, 51, 102, 0.1)',
        borderWidth: 2,
        fill: false,
        tension: 0.1,
        pointRadius: 0,
        pointHoverRadius: 4,
      }
    ];

    if (results.benchmark) {
      datasets.push({
        label: results.fund_names[benchmarkTrCode] || 'Benchmark',
        data: results.benchmark.cumulative_series.map(p => p.value),
        borderColor: '#6c757d',
        backgroundColor: 'rgba(108, 117, 125, 0.1)',
        borderWidth: 2,
        borderDash: [5, 5],
        fill: false,
        tension: 0.1,
        pointRadius: 0,
        pointHoverRadius: 4,
      });
    }

    return { labels, datasets };
  }, [results, benchmarkTrCode]);

  // Format metric display
  const formatMetric = (value, suffix = '%') => {
    if (value === null || value === undefined) return '-';
    return `${value >= 0 ? '' : ''}${value.toFixed(2)}${suffix}`;
  };

  // Calculate difference for comparison
  const getDiff = (portfolioVal, benchmarkVal) => {
    if (portfolioVal === null || benchmarkVal === null) return null;
    return portfolioVal - benchmarkVal;
  };

  return (
    <div className={`portfolio-constructor-app ${theme}`}>
      <div className="constructor-container">
        <h1 className="constructor-title">Portfolio Constructor</h1>

        <div className="constructor-layout">
          {/* Left Panel - Fund Selection */}
          <div className="fund-selection-panel">
            <h2 className="panel-title">Fund Selection</h2>

            <div className="fund-filters">
              <select
                className="asset-class-select"
                value={selectedAssetClass}
                onChange={(e) => setSelectedAssetClass(e.target.value)}
              >
                <option value="">All Asset Classes</option>
                {assetClasses.map(ac => (
                  <option key={ac} value={ac}>{ac}</option>
                ))}
              </select>

              <input
                type="text"
                className="fund-search"
                placeholder="Search funds..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>

            <div className="funds-list">
              {isLoadingFunds ? (
                <div className="loading-spinner">Loading funds...</div>
              ) : Object.keys(groupedFunds).length === 0 ? (
                <div className="no-funds">No funds found</div>
              ) : (
                Object.entries(groupedFunds).map(([assetClass, funds]) => (
                  <div key={assetClass} className="asset-class-group">
                    <div className="asset-class-header">{assetClass}</div>
                    {funds.map(fund => {
                      const isInPortfolio = portfolio.some(f => f.tr_code === fund.tr_code);
                      return (
                        <div key={fund.tr_code} className={`fund-row ${isInPortfolio ? 'in-portfolio' : ''}`}>
                          <div className="fund-info">
                            <span className="fund-name">{fund.name}</span>
                            <span className="fund-dates">
                              {fund.inception_date?.substring(0, 7)} - {fund.last_date?.substring(0, 7)}
                            </span>
                          </div>
                          <button
                            className={`add-btn ${isInPortfolio ? 'added' : ''}`}
                            onClick={() => addToPortfolio(fund)}
                            disabled={isInPortfolio}
                          >
                            {isInPortfolio ? 'Added' : '+ Add'}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Right Panel - Portfolio & Results */}
          <div className="portfolio-panel">
            {/* Portfolio Composition */}
            <div className="portfolio-composition">
              <div className="composition-header">
                <h2 className="panel-title">Portfolio Composition</h2>
                <div className={`total-weight ${Math.abs(totalWeight - 100) < 0.01 ? 'valid' : 'invalid'}`}>
                  Total: {totalWeight.toFixed(1)}%
                </div>
              </div>

              {Math.abs(totalWeight - 100) > 0.01 && portfolio.length > 0 && (
                <div className="weight-warning">
                  ⚠️ Weights must sum to 100%
                </div>
              )}

              {portfolio.length === 0 ? (
                <div className="empty-portfolio">
                  Add funds from the left panel to build your portfolio
                </div>
              ) : (
                <table className="portfolio-table">
                  <thead>
                    <tr>
                      <th>Fund Name</th>
                      <th>Weight (%)</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {portfolio.map(fund => (
                      <tr key={fund.tr_code}>
                        <td className="fund-name-cell">{fund.name}</td>
                        <td className="weight-cell">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.1"
                            value={fund.weight}
                            onChange={(e) => updateWeight(fund.tr_code, e.target.value)}
                            className="weight-input"
                          />
                        </td>
                        <td className="remove-cell">
                          <button
                            className="remove-btn"
                            onClick={() => removeFromPortfolio(fund.tr_code)}
                          >
                            ✕
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {/* Benchmark Selection */}
              <div className="benchmark-section">
                <label className="benchmark-label">Benchmark (optional):</label>
                <div className="benchmark-search-container">
                  <input
                    type="text"
                    className="benchmark-search-input"
                    placeholder="Search for a benchmark fund..."
                    value={benchmarkSearch}
                    onChange={(e) => {
                      setBenchmarkSearch(e.target.value);
                      setShowBenchmarkDropdown(true);
                      if (!e.target.value) {
                        setBenchmarkTrCode('');
                      }
                    }}
                    onFocus={() => setShowBenchmarkDropdown(true)}
                    onBlur={() => {
                      // Delay to allow click on dropdown item
                      setTimeout(() => setShowBenchmarkDropdown(false), 200);
                    }}
                  />
                  {benchmarkTrCode && (
                    <button
                      className="benchmark-clear-btn"
                      onClick={clearBenchmark}
                      title="Clear benchmark"
                    >
                      ✕
                    </button>
                  )}
                  {showBenchmarkDropdown && (
                    <div className="benchmark-dropdown">
                      {filteredBenchmarkFunds.length === 0 ? (
                        <div className="benchmark-dropdown-empty">No funds found</div>
                      ) : (
                        filteredBenchmarkFunds.map(fund => {
                          const isPriority = priorityBenchmarks.has(fund.tr_code);
                          return (
                            <div
                              key={fund.tr_code}
                              className={`benchmark-dropdown-item ${fund.tr_code === benchmarkTrCode ? 'selected' : ''} ${isPriority ? 'priority' : ''}`}
                              onClick={() => selectBenchmark(fund)}
                            >
                              <span className="benchmark-fund-name">
                                {isPriority && <span className="priority-star">★</span>}
                                {fund.name}
                              </span>
                              <span className="benchmark-fund-class">{fund.asset_class_group}</span>
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="action-buttons">
                <button
                  className="calculate-btn"
                  onClick={calculatePortfolio}
                  disabled={portfolio.length === 0 || Math.abs(totalWeight - 100) > 0.01 || isCalculating}
                >
                  {isCalculating ? 'Calculating...' : 'Calculate Portfolio'}
                </button>
                <button
                  className="rebalance-btn"
                  onClick={rebalanceWeights}
                  disabled={portfolio.length === 0}
                  title="Scale weights proportionally to sum to 100%"
                >
                  Rebalance to 100%
                </button>
                <button
                  className="clear-btn"
                  onClick={clearPortfolio}
                  disabled={portfolio.length === 0}
                >
                  Clear All
                </button>
              </div>

              {error && <div className="error-message">{error}</div>}
            </div>

            {/* Results Section */}
            {results && (
              <div className="results-section">
                <div className="results-header">
                  <h2 className="panel-title">Results</h2>
                  <span className="date-range">
                    {results.common_start_date} to {results.common_end_date} ({results.num_months} months)
                  </span>
                </div>

                {/* Chart */}
                <div className="chart-container">
                  {chartData && <Line data={chartData} options={chartOptions} />}
                </div>

                {/* Metrics Table */}
                <table className="metrics-table">
                  <thead>
                    <tr>
                      <th>Metric</th>
                      <th>Portfolio</th>
                      {results.benchmark && <th>Benchmark</th>}
                      {results.benchmark && <th>Difference</th>}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Total Return</td>
                      <td className={results.portfolio.metrics.total_return >= 0 ? 'positive' : 'negative'}>
                        {formatMetric(results.portfolio.metrics.total_return)}
                      </td>
                      {results.benchmark && (
                        <>
                          <td className={results.benchmark.metrics.total_return >= 0 ? 'positive' : 'negative'}>
                            {formatMetric(results.benchmark.metrics.total_return)}
                          </td>
                          <td className={getDiff(results.portfolio.metrics.total_return, results.benchmark.metrics.total_return) >= 0 ? 'positive' : 'negative'}>
                            {formatMetric(getDiff(results.portfolio.metrics.total_return, results.benchmark.metrics.total_return))}
                          </td>
                        </>
                      )}
                    </tr>
                    <tr>
                      <td>Annualised Return</td>
                      <td className={results.portfolio.metrics.annualised_return >= 0 ? 'positive' : 'negative'}>
                        {formatMetric(results.portfolio.metrics.annualised_return)}
                      </td>
                      {results.benchmark && (
                        <>
                          <td className={results.benchmark.metrics.annualised_return >= 0 ? 'positive' : 'negative'}>
                            {formatMetric(results.benchmark.metrics.annualised_return)}
                          </td>
                          <td className={getDiff(results.portfolio.metrics.annualised_return, results.benchmark.metrics.annualised_return) >= 0 ? 'positive' : 'negative'}>
                            {formatMetric(getDiff(results.portfolio.metrics.annualised_return, results.benchmark.metrics.annualised_return))}
                          </td>
                        </>
                      )}
                    </tr>
                    <tr>
                      <td>Volatility</td>
                      <td>{formatMetric(results.portfolio.metrics.volatility)}</td>
                      {results.benchmark && (
                        <>
                          <td>{formatMetric(results.benchmark.metrics.volatility)}</td>
                          <td className={getDiff(results.portfolio.metrics.volatility, results.benchmark.metrics.volatility) <= 0 ? 'positive' : 'negative'}>
                            {formatMetric(getDiff(results.portfolio.metrics.volatility, results.benchmark.metrics.volatility))}
                          </td>
                        </>
                      )}
                    </tr>
                    <tr>
                      <td>Return / Risk</td>
                      <td>{formatMetric(results.portfolio.metrics.sharpe_ratio, '')}</td>
                      {results.benchmark && (
                        <>
                          <td>{formatMetric(results.benchmark.metrics.sharpe_ratio, '')}</td>
                          <td className={getDiff(results.portfolio.metrics.sharpe_ratio, results.benchmark.metrics.sharpe_ratio) >= 0 ? 'positive' : 'negative'}>
                            {formatMetric(getDiff(results.portfolio.metrics.sharpe_ratio, results.benchmark.metrics.sharpe_ratio), '')}
                          </td>
                        </>
                      )}
                    </tr>
                    <tr>
                      <td>Max Drawdown</td>
                      <td className="negative">{formatMetric(results.portfolio.metrics.max_drawdown)}</td>
                      {results.benchmark && (
                        <>
                          <td className="negative">{formatMetric(results.benchmark.metrics.max_drawdown)}</td>
                          <td className={getDiff(results.portfolio.metrics.max_drawdown, results.benchmark.metrics.max_drawdown) >= 0 ? 'positive' : 'negative'}>
                            {formatMetric(getDiff(results.portfolio.metrics.max_drawdown, results.benchmark.metrics.max_drawdown))}
                          </td>
                        </>
                      )}
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Disclaimer Card */}
      <div className="card disclaimer-card mt-3">
        <div className="card-body">
          <div className="disclaimer-text">
            <h6><i className="bi bi-info-circle"></i> Portfolio Backtester &ndash; Important Information</h6>
            <ManagedDisclaimer dkey="tool_backtester" as="p" className="small text-muted mb-0" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortfolioConstructorApp;
